import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
const STAFF_ROLES = ["system_administrator", "hr", "operations", "finance", "executive", "staffing_coordinator", "recruiter"];
const IDLE_MINUTES = 30;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [sessionNotice, setSessionNotice] = useState("");
  const lastActivity = useRef(Date.now());

  useEffect(() => {
    async function restoreSession() {
      try {
        const response = await api.get("/auth/me");
        setUser(response.data);
      } catch (error) {
        window.localStorage.removeItem("tagforce-access-token");
        setUser(false);
      } finally {
        setIsChecking(false);
      }
    }

    restoreSession();
  }, []);

  const endSession = useCallback(async (notice, { revoke = true } = {}) => {
    try {
      if (revoke) await api.post("/auth/logout");
    } catch (error) {
      // the session may already be gone server-side
    } finally {
      window.localStorage.removeItem("tagforce-access-token");
      if (notice) setSessionNotice(notice);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !STAFF_ROLES.includes(user.role)) return undefined;
    const idleMinutes = Number(user.idle_minutes) || IDLE_MINUTES;
    const idleLabel = idleMinutes >= 60 ? `${idleMinutes / 60} hour${idleMinutes === 60 ? "" : "s"}` : `${idleMinutes} minutes`;
    lastActivity.current = Date.now();
    const touch = () => { lastActivity.current = Date.now(); };
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, touch, { passive: true }));
    const check = () => {
      if (Date.now() - lastActivity.current > idleMinutes * 60 * 1000) {
        endSession(`You were signed out after ${idleLabel} of inactivity.`);
      }
    };
    const timer = window.setInterval(check, 30 * 1000);
    // an installed app that comes back from the background is checked immediately, not at the next tick
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    const onForbidden = (event) => {
      // a rejected request must never hard-revoke the server session: clear local state only, so a transient 401 can't sign the user out everywhere
      if (event.detail?.status === 401 && user) endSession("Your session ended. Please sign in again.", { revoke: false });
    };
    window.addEventListener("tagforce:unauthorized", onForbidden);
    return () => {
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, touch));
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("tagforce:unauthorized", onForbidden);
    };
  }, [user, endSession]);

  async function login(credentials) {
    try {
      const response = await api.post("/auth/login", credentials);
      if (response.data.two_factor_required) {
        return {
          twoFactorRequired: true,
          challengeToken: response.data.challenge_token,
          trustedDeviceEligible: Boolean(response.data.trusted_device_eligible),
          deviceAlreadyTrusted: Boolean(response.data.device_already_trusted),
        };
      }
      applySession(response.data);
      return { twoFactorRequired: false };
    } catch (error) {
      throw new Error(formatApiError(error));
    }
  }

  async function verifyTwoFactor(challengeToken, code, trustedDevice = false) {
    try {
      const response = await api.post("/auth/2fa/verify", { challenge_token: challengeToken, code, trusted_device: trustedDevice });
      applySession(response.data);
      return { trustedDevice: Boolean(response.data.trusted_device), idleMinutes: response.data.idle_minutes };
    } catch (error) {
      throw new Error(formatApiError(error));
    }
  }

  async function logout() {
    await endSession("");
  }

  function applySession(data) {
    window.localStorage.setItem("tagforce-access-token", data.access_token);
    setSessionNotice("");
    setUser(data.user);
  }

  function updateUser(patch) {
    setUser((current) => (current ? { ...current, ...patch } : current));
  }

  return (
    <AuthContext.Provider value={{ user, isChecking, sessionNotice, login, verifyTwoFactor, logout, applySession, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}
